/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Dupla, GameState, HistoricoRodada } from "../types";
import { StorageService, STORAGE_KEYS } from "../services/storage";

// Duplas iniciais vazias para o organizador começar do zero
export const DEFAULT_DUPLAS: Dupla[] = [];

export const DEFAULT_GAME_STATE: GameState = {
  emAndamento: false,
  jogoAtual: null,
  primeiraProxima: null,
  equipeIncompleta: "", // Sem jogador solo inicial padrão
  fila: [],
  vitoriasConsecutivas: 0,
  idEquipeVencedoraAtual: null,
  regras: {
    tamanhoEquipe: 2,
    modoPlay: "solo",
    limiteVitorias: 0,
    destinoVencedorLimite: "primeira_proxima",
    nomenclaturaFusao: "misto",
    esquemaRotacao: "desafiante",
  },
};

// Carrega os dados do LocalStorage ou inicializa com os padrões
export function loadRachaoData() {
  try {
    const duplas = StorageService.get<Dupla[]>(STORAGE_KEYS.RACHAO_DUPLAS, DEFAULT_DUPLAS) || DEFAULT_DUPLAS;
    const historico = StorageService.get<HistoricoRodada[]>(STORAGE_KEYS.RACHAO_HISTORICO, []) || [];
    const parsedGameState = StorageService.get<Partial<GameState>>(STORAGE_KEYS.RACHAO_GAME_STATE, null);

    let gameState: GameState = DEFAULT_GAME_STATE;
    if (parsedGameState) {
      gameState = {
        ...DEFAULT_GAME_STATE,
        ...parsedGameState,
        regras: {
          ...DEFAULT_GAME_STATE.regras,
          ...(parsedGameState.regras || {}),
        },
      } as GameState;
    }

    return { duplas, gameState, historico };
  } catch (error) {
    console.error("Erro ao carregar dados do LocalStorage:", error);
    return { duplas: DEFAULT_DUPLAS, gameState: DEFAULT_GAME_STATE, historico: [] };
  }
}

// Salva todos os dados no LocalStorage
export function saveRachaoData(duplas: Dupla[], gameState: GameState, historico: HistoricoRodada[]) {
  try {
    StorageService.set(STORAGE_KEYS.RACHAO_DUPLAS, duplas);
    StorageService.set(STORAGE_KEYS.RACHAO_GAME_STATE, gameState);
    StorageService.set(STORAGE_KEYS.RACHAO_HISTORICO, historico);
  } catch (error) {
    console.error("Erro ao salvar dados no LocalStorage:", error);
  }
}

// Exporta os dados para formato JSON
export function exportToJSON(duplas: Dupla[], gameState: GameState, historico: HistoricoRodada[]) {
  const dataStr = JSON.stringify({ duplas, gameState, historico }, null, 2);
  const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
  
  const exportFileDefaultName = `rachao_volei_backup_${new Date().toISOString().slice(0, 10)}.json`;
  
  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", dataUri);
  linkElement.setAttribute("download", exportFileDefaultName);
  linkElement.click();
}

// Exporta o histórico e estatísticas para um arquivo CSV legível no Excel (com BOM para acentos em português)
export function exportToExcel(historico: HistoricoRodada[], duplas: Dupla[]) {
  // Criação do conteúdo do CSV
  let csvContent = "\uFEFF"; // Byte Order Mark (BOM) para o Excel abrir com acentuação correta em português (UTF-8)
  
  // Cabeçalho da tabela de Histórico
  csvContent += "HISTÓRICO DE PARTIDAS\n";
  csvContent += "Rodada;Dupla 1;Dupla 2;Vencedora;Perdedora;Jogador Transferido (Dupla);Novo Jogador Solo;Horário\n";
  
  historico.forEach((r) => {
    csvContent += `${r.rodada};${r.dupla1};${r.dupla2};${r.vencedora};${r.perdedora};${r.jogadorTransferido};${r.primeiraProxima};${r.horario}\n`;
  });
  
  csvContent += "\n\n";
  
  // Calcular Estatísticas para anexar no relatório
  csvContent += "ESTATÍSTICAS DO RACHÃO\n";
  csvContent += "Classificação;Dupla;Jogador 1;Jogador 2;Jogos Disputados;Vitórias;Derrotas;Aproveitamento (%)\n";
  
  // Cálculo de estatísticas idêntico ao do painel
  const statsMap = new Map<string, { v: number; j: number; d: string; p1: string; p2: string }>();
  
  // Inicializa com as duplas existentes
  duplas.forEach(d => {
    statsMap.set(d.name, { v: 0, j: 0, d: d.name, p1: d.player1, p2: d.player2 });
  });
  
  // Adiciona do histórico
  historico.forEach(h => {
    // Dupla 1
    if (!statsMap.has(h.dupla1)) {
      statsMap.set(h.dupla1, { v: 0, j: 0, d: h.dupla1, p1: "Jogador 1", p2: "Jogador 2" });
    }
    statsMap.get(h.dupla1)!.j += 1;
    
    // Dupla 2
    if (!statsMap.has(h.dupla2)) {
      statsMap.set(h.dupla2, { v: 0, j: 0, d: h.dupla2, p1: "Jogador 1", p2: "Jogador 2" });
    }
    statsMap.get(h.dupla2)!.j += 1;
    
    // Vencedor
    if (!statsMap.has(h.vencedora)) {
      statsMap.set(h.vencedora, { v: 0, j: 0, d: h.vencedora, p1: "Jogador 1", p2: "Jogador 2" });
    }
    statsMap.get(h.vencedora)!.v += 1;
  });
  
  const statsArray = Array.from(statsMap.values()).sort((a, b) => {
    if (b.v !== a.v) return b.v - a.v;
    const rateB = a.j > 0 ? (b.v / b.j) : 0;
    const rateA = b.j > 0 ? (a.v / a.j) : 0;
    return rateB - rateA;
  });
  
  statsArray.forEach((s, index) => {
    const derrotas = s.j - s.v;
    const aproveitamento = s.j > 0 ? ((s.v / s.j) * 100).toFixed(1) : "0.0";
    csvContent += `${index + 1};${s.d};${s.p1};${s.p2};${s.j};${s.v};${derrotas};${aproveitamento}%\n`;
  });
  
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const linkElement = document.createElement("a");
  linkElement.setAttribute("href", url);
  linkElement.setAttribute("download", `rachao_volei_relatorio_${new Date().toISOString().slice(0, 10)}.csv`);
  linkElement.click();
}
